import type { Ace } from 'ace-builds';

import type { Endomorphism } from '@code-expert/prelude';
import { eq, iots, number, string, struct } from '@code-expert/prelude';
import type { Cursor } from '/imports/ui/components/AceEditor/Cursor';

export interface BlankIdBrand {
  readonly BlankId: unique symbol;
}

export const BlankId = iots.brand(
  iots.string,
  (s): s is iots.Branded<string, BlankIdBrand> => string.isNotBlank(s),
  'BlankId',
);

export type BlankId = iots.TypeOf<typeof BlankId>;

export const blankIdEq: eq.Eq<BlankId> = string.Eq;

export const blankEqById: eq.Eq<{ id: BlankId }> = eq.struct({
  id: blankIdEq,
});

export const blankEqByIdAndValue: eq.Eq<Blank> = eq.struct({
  id: blankIdEq,
  value: string.Eq,
});

/** zero-based, i.e. top left = 0,0 */
export const BlankPositionC = iots.strict({
  startRow: iots.number,
  startCol: iots.number,
  endRow: iots.number,
  endCol: iots.number,
});

export type BlankPosition = iots.TypeOf<typeof BlankPositionC>;

export const blankPositionEqByStart: eq.Eq<Pick<BlankPosition, 'startCol' | 'startRow'>> =
  eq.struct({
    startRow: number.Eq,
    startCol: number.Eq,
  });

export const BlankC = iots.strict({
  id: BlankId,
  value: iots.string,
  position: BlankPositionC,
  contentPosition: BlankPositionC,
  contentWithAttributesPosition: BlankPositionC,
});

export type Blank = iots.TypeOf<typeof BlankC>;

export const isSelected =
  (cursor: Cursor) =>
  ({ position }: Blank) =>
    cursor.row >= position.startRow &&
    cursor.row <= position.endRow &&
    cursor.column >= position.startCol &&
    cursor.column <= position.endCol;

export const editorRangeFromBlankPosition = ({
  endCol,
  endRow,
  startCol,
  startRow,
}: BlankPosition): Ace.IRange => ({
  start: { column: startCol, row: startRow },
  end: { column: endCol, row: endRow },
});

export const addToBlankPosition = ({
  startCol = 0,
  startRow = 0,
  endCol = 0,
  endRow = 0,
}: Partial<BlankPosition>): Endomorphism<BlankPosition> =>
  struct.evolve({
    startRow: number.add(startRow),
    startCol: number.add(startCol),
    endRow: number.add(endRow),
    endCol: number.add(endCol),
  });

export const solutionPositionFromContentPosition = addToBlankPosition({ startCol: 1, endCol: -1 });
